"use client"

import { PriceQuote } from "@/app/offers/price-quote"
import { useOfferValidation } from "@/app/offers/use-offer-validation"
import { useSolanaCollectionOffer } from "@/app/sdk/action-molecules/solana/solana-collection-offer-molecule"
import { Trans } from "@lingui/react/macro"
import {
  Button,
  DialogHeader,
  DialogTitle,
} from "@rarible_int/anaconda_ui_components"
import { atom } from "@reatom/core"
import { reatomComponent } from "@reatom/react"
import { useMemo, useState } from "react"
import {
  useCollection,
  useCollectionNativeCurrency,
  useCollectionStatistic,
} from "../store/use-collection"
import { CollectionPreview } from "./collection-preview"
import { OfferPriceInput } from "./price-input"

interface SolanaModernCollectionOfferDialogContentProps {
  onOpenChange: (isOpened: boolean) => void
}

export const SolanaModernCollectionOfferDialogContent =
  reatomComponent<SolanaModernCollectionOfferDialogContentProps>(
    ({ onOpenChange }) => {
      const collection = useCollection()
      const [quantity] = useState<number>(1)
      const currency = useCollectionNativeCurrency()
      const { mutate } = useSolanaCollectionOffer(collection.id)

      const { priceAtom } = useMemo(() => {
        const priceAtom = atom<number | undefined>(
          undefined,
          "solanaOfferPrice",
        )

        return { priceAtom }
      }, [])

      const price = priceAtom()
      const setPrice = (value: number | undefined) => priceAtom.set(value)
      const collectionStatistic = useCollectionStatistic()
      const { error } = useOfferValidation({
        price,
        quantity,
        currency,
        floor: collectionStatistic?.floor,
      })

      return (
        <form
          className="flex flex-col gap-y-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (price === undefined) return
            mutate({ price, currency: currency.id })
            onOpenChange(false)
          }}
        >
          <DialogHeader className="pb-2">
            <DialogTitle className="text-center text-lg">
              <Trans>Collection offer</Trans>
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <CollectionPreview collection={collection} />
            <OfferPriceInput
              onPriceChange={setPrice}
              error={error}
              currency={currency}
            />
            <PriceQuote
              quantity={quantity}
              price={{ amount: price?.toString() ?? "0", currency }}
            />
          </div>
          <Button
            className="flex-grow"
            type="submit"
            disabled={!price || !!error}
          >
            <Trans>Make a collection offer</Trans>
          </Button>
        </form>
      )
    },
    "SolanaModernCollectionOfferDialogContent",
  )
